/**
 * Wizard "Importa i tuoi prezzi" — Fase 4.
 *
 * Flusso: l'utente esporta i materiali in Excel, compila le colonne
 * "Il tuo prezzo (€)" / "Il tuo tempo (min)", e reimporta il file qui.
 * Il wizard NON scrive nulla finché non si conferma: mostra un'anteprima con
 * valore attuale ▸ valore dal file, e si sceglie riga per riga cosa applicare.
 *
 * Scrive PERSONALIZZAZIONI per-org (non tocca il catalogo condiviso), quindi è
 * sicuro anche sui materiali di altri fornitori.
 */
import React, { useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Upload, FileSpreadsheet, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { DatabaseMaterial } from '@/hooks/useMaterials';
import { useSaveMaterialCustomization } from '@/hooks/useMaterialCustomizations';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Materiali attualmente caricati (per il match per codice e i valori correnti). */
  materials: DatabaseMaterial[];
}

interface PreviewRow {
  materialId: string;
  code: string;
  name: string;
  currentPrice: number | null;
  newPrice: number | null;
  currentMinutes: number | null;
  newMinutes: number | null;
  /** true se il file propone almeno un cambiamento reale */
  changed: boolean;
  apply: boolean;
}

const num = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};
const fmt = (n: number | null) => (n == null ? '—' : String(Math.round(n * 1e4) / 1e4));

const ImportCustomizationsWizard: React.FC<Props> = ({ open, onOpenChange, materials }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<PreviewRow[] | null>(null);
  const [notFound, setNotFound] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const { mutateAsync: saveCustomization } = useSaveMaterialCustomization();

  const byCode = useMemo(() => {
    const m = new Map<string, DatabaseMaterial>();
    for (const mat of materials) if (mat.code) m.set(String(mat.code).trim(), mat);
    return m;
  }, [materials]);

  const handleFile = async (file: File) => {
    try {
      const XLSX = await import('xlsx');
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });

      const preview: PreviewRow[] = [];
      const missing: string[] = [];

      for (const r of json) {
        const code = String(r['Codice'] ?? '').trim();
        if (!code) continue;
        const mat = byCode.get(code);
        if (!mat) { missing.push(code); continue; }

        const newPrice = num(r['Il tuo prezzo (€)'] ?? r['Il tuo prezzo']);
        const newMinutes = num(r['Il tuo tempo (min)'] ?? r['Il tuo tempo']);

        const currentPrice = mat.custom_net_price ?? null;
        const currentMinutes =
          mat.custom_installation_time_per_sqm != null
            ? Math.round(mat.custom_installation_time_per_sqm * 60 * 1e4) / 1e4
            : null;

        const changed =
          (newPrice ?? null) !== (currentPrice ?? null) ||
          (newMinutes ?? null) !== (currentMinutes ?? null);

        preview.push({
          materialId: mat.id, code, name: mat.name,
          currentPrice, newPrice, currentMinutes, newMinutes,
          changed, apply: changed,
        });
      }

      setRows(preview);
      setNotFound(missing);
      if (preview.length === 0) {
        toast.warning('Nessuna riga utilizzabile', {
          description: 'Serve la colonna "Codice" e almeno una tra "Il tuo prezzo (€)" e "Il tuo tempo (min)".',
        });
      }
    } catch (e) {
      toast.error(`File non leggibile: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const toApply = (rows ?? []).filter((r) => r.apply && r.changed);

  const handleConfirm = async () => {
    if (toApply.length === 0) return;
    setBusy(true);
    setProgress(0);
    let ok = 0, fail = 0;
    for (let i = 0; i < toApply.length; i++) {
      const r = toApply[i];
      try {
        await saveCustomization({
          materialId: r.materialId,
          customNetPrice: r.newPrice,
          customInstallationTimeHours: r.newMinutes != null ? r.newMinutes / 60 : null,
        });
        ok++;
      } catch { fail++; }
      setProgress(Math.round(((i + 1) / toApply.length) * 100));
    }
    setBusy(false);
    toast.success(`Personalizzazioni applicate: ${ok}${fail ? ` · non riuscite: ${fail}` : ''}`);
    setRows(null);
    onOpenChange(false);
  };

  const reset = () => { setRows(null); setNotFound([]); setProgress(0); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4 text-primary" />
            Importa i tuoi prezzi e tempi da Excel
          </DialogTitle>
          <DialogDescription>
            Esporta i materiali, compila le colonne <strong>“Il tuo prezzo (€)”</strong> e
            <strong> “Il tuo tempo (min)”</strong>, poi ricarica qui il file. Nessuna modifica
            viene salvata prima della conferma, e il catalogo condiviso non viene toccato.
          </DialogDescription>
        </DialogHeader>

        {!rows && (
          <div className="border-2 border-dashed rounded-lg p-8 text-center space-y-3">
            <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Seleziona il file .xlsx compilato</p>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
            />
            <Button onClick={() => fileRef.current?.click()}>Scegli file…</Button>
          </div>
        )}

        {rows && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant="secondary">{rows.length} righe riconosciute</Badge>
              <Badge variant="outline" className="border-primary text-primary">
                {rows.filter((r) => r.changed).length} con modifiche
              </Badge>
              {notFound.length > 0 && (
                <Badge variant="outline" className="border-amber-500 text-amber-600">
                  <AlertTriangle className="h-3 w-3 mr-1 inline" />
                  {notFound.length} codici non trovati (ignorati)
                </Badge>
              )}
            </div>

            <div className="max-h-[45vh] overflow-auto border rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="p-2 text-left w-10">
                      <Checkbox
                        checked={rows.filter(r => r.changed).every(r => r.apply) && rows.some(r => r.changed)}
                        onCheckedChange={(v) =>
                          setRows(rows.map(r => (r.changed ? { ...r, apply: !!v } : r)))
                        }
                        aria-label="Seleziona tutte le modifiche"
                      />
                    </th>
                    <th className="p-2 text-left">Codice</th>
                    <th className="p-2 text-left">Materiale</th>
                    <th className="p-2 text-right">Prezzo attuale ▸ nuovo</th>
                    <th className="p-2 text-right">Tempo attuale ▸ nuovo</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.materialId} className={r.changed ? '' : 'opacity-45'}>
                      <td className="p-2">
                        <Checkbox
                          checked={r.apply && r.changed}
                          disabled={!r.changed}
                          onCheckedChange={(v) =>
                            setRows(rows.map(x => (x.materialId === r.materialId ? { ...x, apply: !!v } : x)))
                          }
                        />
                      </td>
                      <td className="p-2 font-mono">{r.code}</td>
                      <td className="p-2 max-w-[240px] truncate" title={r.name}>{r.name}</td>
                      <td className="p-2 text-right whitespace-nowrap">
                        <span className="text-muted-foreground">{fmt(r.currentPrice)}</span>
                        {' ▸ '}
                        <strong className={r.newPrice !== r.currentPrice ? 'text-primary' : ''}>{fmt(r.newPrice)}</strong>
                      </td>
                      <td className="p-2 text-right whitespace-nowrap">
                        <span className="text-muted-foreground">{fmt(r.currentMinutes)}</span>
                        {' ▸ '}
                        <strong className={r.newMinutes !== r.currentMinutes ? 'text-primary' : ''}>{fmt(r.newMinutes)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-[11px] text-muted-foreground">
              Cella vuota = nessuna personalizzazione (torna al prezzo/tempo di catalogo).
              Le righe senza modifiche sono in grigio e non verranno toccate.
            </p>
            {busy && <p className="text-xs text-primary">Applicazione in corso… {progress}%</p>}
          </div>
        )}

        <DialogFooter>
          {rows && <Button variant="ghost" onClick={reset} disabled={busy}>Cambia file</Button>}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annulla</Button>
          {rows && (
            <Button onClick={handleConfirm} disabled={busy || toApply.length === 0}>
              {busy ? 'Applicazione…' : `Applica ${toApply.length} modifiche`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportCustomizationsWizard;
